import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Session, User } from '@supabase/supabase-js';

export type ViewMode = 'supervisor' | 'user';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: string | null;
  isSupervisor: boolean;
  viewMode: ViewMode;
  isSupervisorMode: boolean;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  role: null,
  isSupervisor: false,
  viewMode: 'supervisor',
  isSupervisorMode: false,
  setViewMode: () => { },
  toggleViewMode: () => { },
  loading: true,
  signOut: async () => { },
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem('jatt_view_mode');
      if (saved === 'user' || saved === 'supervisor') return saved;
    } catch {
      // ignore
    }
    return 'supervisor';
  });
  const [loading, setLoading] = useState(true);

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode);
    try {
      localStorage.setItem('jatt_view_mode', mode);
    } catch {
      // ignore
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === 'supervisor' ? 'user' : 'supervisor');
  };

  const isSupervisor = role === 'supervisor';
  const isSupervisorMode = isSupervisor && viewMode === 'supervisor';

  useEffect(() => {
    const handleAuthChange = async (currentSession: Session | null) => {
      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', currentUser.id)
            .single();
          if (data && !error) {
            setRole(data.role);
          } else {
            setRole('user');
          }
        } catch {
          setRole('user');
        }
      } else {
        setRole(null);
      }
      setLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthChange(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      handleAuthChange(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, role, isSupervisor, viewMode, isSupervisorMode, setViewMode, toggleViewMode, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
